import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode
} from 'react';

type AlertType = 'success' | 'error' | 'info' | 'warning';

type SubsAlertType = 'coins' | 'users' | 'pages';

interface AlertInfo {
  showAlert: boolean;
  message: string;
  alertType: AlertType;
}

interface SubsAlertInfo {
  showAlert: boolean;
  message: string;
  alertType: SubsAlertType;
}

interface AlertContextValue {
  mainAlert: AlertInfo;
  clearAlert: () => void;
  setAlert: (message: string, type: AlertType, timeoutSeconds?: number) => void;
  handleError: (error: unknown) => void; // ← Add this
  subsMainAlert: SubsAlertInfo;
  clearSubsAlert: () => void;
  setSubsAlert: (
    message: string,
    type: SubsAlertType,
    timeoutSeconds?: number
  ) => void;
}

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

export const AlertProvider = ({ children }: { children: ReactNode }) => {
  const [mainAlert, setMainAlert] = useState<AlertInfo>({
    showAlert: false,
    message: '',
    alertType: 'success'
  });
  const [subsMainAlert, setMainSubsAlert] = useState<SubsAlertInfo>({
    showAlert: false,
    message: '',
    alertType: 'users'
  });

  const clearAlert = useCallback(() => {
    setMainAlert((prev) => ({
      ...prev,
      showAlert: false,
      message: '',
      alertType: 'success'
    }));
  }, []);

  const setAlert = useCallback(
    (message: string, type: AlertType = 'info', timeoutSeconds?: number) => {
      setMainAlert({ showAlert: true, message, alertType: type });

      if (timeoutSeconds && timeoutSeconds > 0) {
        setTimeout(() => {
          clearAlert();
        }, timeoutSeconds * 1000);
      }
    },
    [clearAlert]
  );

  const clearSubsAlert = useCallback(() => {
    setMainSubsAlert((prev) => ({
      ...prev,
      showAlert: false,
      message: '',
      alertType: 'coins'
    }));
  }, []);

  const setSubsAlert = useCallback(
    (
      message: string,
      type: SubsAlertType = 'coins',
      timeoutSeconds?: number
    ) => {
      setMainSubsAlert({ showAlert: true, message, alertType: type });

      if (timeoutSeconds && timeoutSeconds > 0) {
        setTimeout(() => {
          clearSubsAlert();
        }, timeoutSeconds * 1000);
      }
    },
    [clearSubsAlert]
  );

  const handleError = useCallback((error: unknown) => {
    console.error('Caught error:', error);

    let message = 'An unexpected error occurred.';
    let alertType: AlertType = 'error';

    // Handle Axios-style errors
    if (typeof error === 'object' && error !== null) {
      const err = error as any;

      if (err.response?.status) {
        // HTTP error with response code
        switch (err.response.status) {
          case 400:
            message = 'Bad request.';
            break;
          case 401:
            message = 'Unauthorized. Please log in again.';
            break;
          case 403:
            message = 'You do not have permission to perform this action.';
            break;
          case 404:
            message = 'Resource not found.';
            break;
          case 500:
            message = 'Server error. Please try again later.';
            break;
          default:
            message = `Unexpected error (${err.response.status}).`;
        }
      } else if (err.name === 'NotAuthorizedException') {
        message = 'Session expired. Please sign in again.';
      } else if (err.code === 'NetworkingError') {
        message = 'Network error. Please check your internet connection.';
      } else if (err.message) {
        message = err.message;
      }
    }

    setMainAlert({
      showAlert: true,
      message,
      alertType
    });
  }, []);

  return (
    <AlertContext.Provider
      value={{
        mainAlert,
        clearAlert,
        setAlert,
        handleError,
        subsMainAlert,
        clearSubsAlert,
        setSubsAlert
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextValue => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
